import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../components/layout/AdminLayout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  User,
  Building2,
  Banknote,
  FileText,
  Loader2,
  Search,
} from "lucide-react";

export default function AdminPendingPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payments/pending`, {
        withCredentials: true,
      });
      setPayments(response.data.payments || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load pending payments");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/payments/${selectedPayment.payment_id}/approve`,
        { notes: notes || null },
        { withCredentials: true }
      );
      toast.success("Payment approved successfully!");
      setSelectedPayment(null);
      setActionType(null);
      setNotes("");
      fetchPendingPayments();
    } catch (error) {
      console.error("Error approving payment:", error);
      toast.error(error.response?.data?.detail || "Failed to approve payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPayment) return;
    if (!notes.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/payments/${selectedPayment.payment_id}/reject`,
        { notes },
        { withCredentials: true }
      );
      toast.success("Payment rejected");
      setSelectedPayment(null);
      setActionType(null);
      setNotes("");
      fetchPendingPayments();
    } catch (error) {
      console.error("Error rejecting payment:", error);
      toast.error(error.response?.data?.detail || "Failed to reject payment");
    } finally {
      setProcessing(false);
    }
  };

  const openActionModal = (payment, type) => {
    setSelectedPayment(payment);
    setActionType(type);
    setNotes("");
  };

  const closeModal = () => {
    setSelectedPayment(null);
    setActionType(null);
    setNotes("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPrice = (amount, currency) => {
    const symbols = { USD: "$", BBD: "BBD $", CAD: "CAD $", GBP: "£" };
    return `${symbols[currency] || "$"}${amount?.toFixed(2) || "0.00"}`;
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case "bank_transfer":
        return <Building2 className="h-4 w-4" />;
      case "firstpay":
        return <Banknote className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getMethodName = (method) => {
    switch (method) {
      case "bank_transfer":
        return "Bank Transfer";
      case "firstpay":
        return "FirstPay";
      default:
        return method;
    }
  };

  const filteredPayments = payments.filter((payment) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.order_id?.toLowerCase().includes(searchLower) ||
      payment.payment_id?.toLowerCase().includes(searchLower) ||
      payment.payer_name?.toLowerCase().includes(searchLower) ||
      payment.transfer_reference?.toLowerCase().includes(searchLower) ||
      payment.customer?.email?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="pending-payments-title">
              <Clock className="inline h-6 w-6 mr-2 text-orange-600" />
              Pending Payments
            </h1>
            <p className="text-slate-600 mt-1">
              {total} payment{total !== 1 ? "s" : ""} awaiting verification
            </p>
          </div>
          <Button variant="outline" onClick={fetchPendingPayments}>
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by order ID, reference, name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-payments"
          />
        </div>

        {/* Payments List */}
        {filteredPayments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No Pending Payments</h3>
            <p className="text-slate-500 mt-1">All payments have been processed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPayments.map((payment) => (
              <div
                key={payment.payment_id}
                className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`payment-card-${payment.payment_id}`}
              >
                <div className="flex items-start justify-between">
                  {/* Payment Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="flex items-center gap-1">
                        {getMethodIcon(payment.payment_method)}
                        {getMethodName(payment.payment_method)}
                      </Badge>
                      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                        Pending Verification
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                      <div>
                        <span className="text-slate-500">Order ID:</span>{" "}
                        <span className="font-medium">{payment.order_id}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Amount:</span>{" "}
                        <span className="font-bold text-green-600">
                          {formatPrice(payment.amount, payment.currency)}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Payer:</span>{" "}
                        <span className="font-medium">{payment.payer_name}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Reference:</span>{" "}
                        <span className="font-mono text-xs">{payment.transfer_reference}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Customer:</span>{" "}
                        <span className="font-medium">{payment.customer?.email}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Submitted:</span>{" "}
                        <span>{formatDate(payment.created_at)}</span>
                      </div>
                    </div>

                    {/* Receipt Link */}
                    {payment.receipt_url && (
                      <div className="mt-2">
                        <a
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <FileText className="h-4 w-4" />
                          View Receipt
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => openActionModal(payment, "approve")}
                      data-testid={`approve-${payment.payment_id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => openActionModal(payment, "reject")}
                      data-testid={`reject-${payment.payment_id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Modal */}
        <Dialog open={!!selectedPayment && !!actionType} onOpenChange={closeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === "approve" ? (
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    Approve Payment
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-5 w-5" />
                    Reject Payment
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {selectedPayment && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Order:</span>
                    <span className="font-medium">{selectedPayment.order_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Amount:</span>
                    <span className="font-bold text-green-600">
                      {formatPrice(selectedPayment.amount, selectedPayment.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Payer:</span>
                    <span>{selectedPayment.payer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Reference:</span>
                    <span className="font-mono text-xs">{selectedPayment.transfer_reference}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {actionType === "approve" ? "Notes (optional)" : "Rejection Reason (required)"}
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                      actionType === "approve"
                        ? "Add any notes about this approval..."
                        : "Explain why this payment is being rejected..."
                    }
                    rows={3}
                    data-testid="action-notes"
                  />
                </div>

                {actionType === "approve" && (
                  <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700">
                    <strong>Note:</strong> Approving this payment will:
                    <ul className="list-disc list-inside mt-1">
                      <li>Lock the order snapshot (FX, shipping, customs, profit)</li>
                      <li>Set order status to PAID_LOCKED</li>
                      <li>Allow order fulfillment</li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeModal} disabled={processing}>
                Cancel
              </Button>
              {actionType === "approve" ? (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleApprove}
                  disabled={processing}
                  data-testid="confirm-approve"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    "Approve Payment"
                  )}
                </Button>
              ) : (
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleReject}
                  disabled={processing}
                  data-testid="confirm-reject"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    "Reject Payment"
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
